import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import {
  Input,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Checkbox,
  Tooltip,
  Form,
  Divider,
  Modal,
  Image,
  message,
  Typography,
} from 'antd'
import styled from 'styled-components'
import { useSelections } from 'ahooks'
import {
  FilterForm,
  DeleteModal,
  SignTitle,
  FilterFormSelect,
  TagSearchOption,
  BasSwitch,
  IconFont,
} from '@/components'
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  FilterDropdownProps,
} from 'antd/lib/table/interface'
import {
  useGetTemplateList,
  useGetCaseList,
  useSaveCaseToTemplate,
  useGetAllTemplateId,
  useGetAllCaseId,
  useAttackTypeList,
  useResourceTagList,
  useGetResourceIdsByTemplateIds,
} from './utils'
import type {
  GetTemplateList,
  GetCaseList,
  SaveCaseToTemplate,
  TemplateResponse,
} from '@/api/waf-create-task'
import { CaseDetail } from './caseDetail'
import { FilterParamsSortOrder } from '@/api/waf-create-task'
import type { ColumnType } from 'antd/es/table'
import leakSeverity from '@/assets/imges/leak-severity.png'
import leakCenter from '@/assets/imges/leak-center.png'
import leakLow from '@/assets/imges/leak-low.png'
import leakHigh from '@/assets/imges/leak-high.png'
import { t } from '@/i18n'
const { Search } = Input
const { useForm } = Form
const { Text } = Typography

// 定义模板类型
interface TemplateItem {
  templateId: string
  name: string
  type: 'system' | 'custom'
  executeUpdate: boolean
  executed: number
  resourceAmount: number
  updateTime: string
  total?: number
  description: string
  originType: string
  resourceIds: string[]
  sceneIds: number[]
  verificationModeIds: number[]
  key: string
  selectedCount?: number
}

interface CaseItem {
  resourceId: number
  key: number
  name: string
  originType: string
  verificationModeId: number
  attackTypeId: string
  riskLevel: string
  executionSceneId: number
  resourceLevel: string
  updateTime: string
  type?: 'system' | 'custom'
  templateIds?: string[]
}

interface WAFCreateTemplateProps {
  onCaseIdsChange?: (caseIds: string[]) => void
  onTemplateIdsChange?: (templates: TemplateItem[]) => void
  executeTemplateLatestCase?: boolean
  templateState?: {
    selectedTemplateIds: string[]
    selectedCaseIds: string[]
    executeTemplateLatestCase: boolean
    selectedTemplateId?: string
    templateSwitchStates: Record<string, boolean>
  }
  onTemplateStateChange?: (newState: {
    selectedTemplateIds?: string[]
    selectedCaseIds?: string[]
    executeTemplateLatestCase?: boolean
    selectedTemplateId?: string
    templateSwitchStates: Record<string, boolean>
  }) => void
  executeType?: number
}

export const WAFCreateTemplate: React.FC<WAFCreateTemplateProps> = ({
  onCaseIdsChange,
  onTemplateIdsChange,
  executeTemplateLatestCase = true,
  templateState,
  onTemplateStateChange,
  executeType,
}) => {
  const [form] = useForm()
  const [searchTemplateParams, setSearchTemplateParams] = useState<GetTemplateList>({
    page: 1,
    pageSize: 10,
  })
  const [searchCaseParams, setSearchCaseParams] = useState<GetCaseList>({
    page: 1,
    pageSize: 10,
  })
  const [filterTemplateParams, setFilterTemplateParams] =
    useState<{ [k: string]: string | number | undefined }>()
  const [filterCaseParams, setFilterCaseParams] =
    useState<{ [k: string]: string | number | undefined }>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])
  const [getTemplateList, templateLoading] = useGetTemplateList()
  const [getAllTemplateId] = useGetAllTemplateId()
  const [attackTypeList, getAttackTypeList] = useAttackTypeList()
  const [resourceIdsByTemplateIds, getResourceIdsByTemplateIds] = useGetResourceIdsByTemplateIds()
  const [resourceTagList, getResourceTagList] = useResourceTagList()
  const [templateIds, setTemplateIds] = useState<string[]>([])
  const [caseIds, setCaseIds] = useState<string[]>([])
  // 维护每个模板的已选用例数映射
  const [templateSelectedCountMap, setTemplateSelectedCountMap] = useState<Record<string, number>>(
    {},
  )
  const [getCaseList, caseLoading] = useGetCaseList()
  const [saveCaseToTemplate, saveCaseToTemplateLoading] = useSaveCaseToTemplate()
  const [templateValue, setTemplateValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })
  const [caseValue, setCaseValue] = useState<{
    field: string | number
    value: string | number | string[] | number[] | undefined
  }>({
    field: '',
    value: '',
  })

  const [templateData, setTemplateData] = useState<{
    list: TemplateItem[]
    total: number
  }>({
    list: [],
    total: 0,
  })

  const [caseData, setCaseData] = useState<{
    list: CaseItem[]
    total: number
  }>({
    list: [],
    total: 0,
  })

  const [saveTemplateModal, setSaveTemplateModal] = useState(false)
  const [detailVisible, setDetailVisible] = useState({ visible: false, resourceId: '' })
  const [isInitialized, setIsInitialized] = useState(false)

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>(
    templateState?.selectedTemplateIds || [],
  )
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>(
    templateState?.selectedCaseIds || [],
  )

  const [selectedTemplateData, setSelectedTemplateData] = useState<TemplateItem[]>([])
  const [caseKey, setCaseKey] = useState(0)
  const [attackTypeSelectOpen, setAttackTypeSelectOpen] = useState(false)
  const [templateTypeSelectOpen, setTemplateTypeSelectOpen] = useState(false)
  const [riskLevelSelectOpen, setRiskLevelSelectOpen] = useState(false)
  const [resourceLevelSelectOpen, setResourceLevelSelectOpen] = useState(false)

  // 监听 executeType 变化
  useEffect(() => {
    if (executeType === 4) {
      // 如果是高频任务，清空已选择的用例
      setSelectedCaseIds([])
      onCaseIdsChange?.([])
      onTemplateStateChange?.({
        selectedTemplateIds: selectedTemplateIds,
        selectedCaseIds: [],
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    }
  }, [executeType])

  // 监听 templateState 变化
  useEffect(() => {
    if (templateState) {
      setSelectedTemplateIds(templateState.selectedTemplateIds || [])
      setSelectedCaseIds(templateState.selectedCaseIds || [])
      // 不需要在这里调用 getCaseList，因为 selectedTemplateIds 变化会触发第388行的 useEffect
    }
  }, [templateState])

  // 监听 selectedTemplateIds 变化，同步更新 selectedTemplateData
  useEffect(() => {
    // 检查是否是"全选所有"的情况
    const isSelectAllAllMode =
      selectedTemplateIds.length === templateIds.length && templateIds.length > 0

    if (selectedTemplateIds.length > 0 && templateData.list.length > 0) {
      // 如果是"全选所有"的情况，不需要更新selectedTemplateData，因为handleSelectAll已经处理了
      if (isSelectAllAllMode) {
        return
      }

      // 从当前模板数据中找出选中的模板
      const newSelectedTemplateData = templateData.list
        .filter(item => selectedTemplateIds.includes(item.templateId))
        .map(item => ({
          ...item,
          executeUpdate:
            templateState?.templateSwitchStates?.[item.templateId] ?? item.executeUpdate,
        }))

      // 合并之前选中的模板数据（不在当前页的）
      const updatedTemplateData = selectedTemplateData
        .filter(item => !templateData.list.some(t => t.templateId === item.templateId))
        .concat(newSelectedTemplateData)

      setSelectedTemplateData(updatedTemplateData)

      // 通知父组件更新状态
      onTemplateIdsChange?.(updatedTemplateData)
    } else if (selectedTemplateIds.length === 0) {
      // 如果没有选中的模板，清空selectedTemplateData
      setSelectedTemplateData([])
      onTemplateIdsChange?.([])
    }
  }, [selectedTemplateIds, templateData.list, templateIds])

  // 监听模板数据变化，如果选中的模板数据变化，重新获取用例数据
  const prevTemplateDataRef = useRef<string>('')
  useEffect(() => {
    if (selectedTemplateIds.length > 0) {
      // 获取当前选中模板的数据摘要（用于检测变化）
      const currentTemplateDataStr = JSON.stringify(
        templateData.list
          .filter(item => selectedTemplateIds.includes(item.templateId))
          .map(item => ({
            templateId: item.templateId,
            resourceAmount: item.resourceAmount,
          }))
          .sort((a, b) => a.templateId.localeCompare(b.templateId)),
      )

      // 如果模板数据变化了，且不是重置参数的情况，重新获取用例数据
      if (
        prevTemplateDataRef.current !== currentTemplateDataStr &&
        prevTemplateDataRef.current !== '' &&
        !isResettingRef.current
      ) {
        // 更新 ref
        prevTemplateDataRef.current = currentTemplateDataStr
        // 清空搜索参数 ref，强制触发用例数据重新获取
        prevSearchCaseParamsRef.current = ''
      } else if (prevTemplateDataRef.current === '') {
        // 首次设置
        prevTemplateDataRef.current = currentTemplateDataStr
      }
    } else {
      prevTemplateDataRef.current = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateData.list, selectedTemplateIds.join(',')])

  const riskLevelMap = {
    RiskLevelLow: { label: t('低危'), class: 'leak-low', icon: <Image src={leakLow} width={19} /> },
    RiskLevelMiddle: {
      label: t('中危'),
      class: 'leak-center',
      icon: <Image src={leakCenter} width={19} />,
    },
    RiskLevelHigh: {
      label: t('高危'),
      class: 'leak-high',
      icon: <Image src={leakHigh} width={19} />,
    },
    RiskLevelGross: {
      label: t('严重'),
      class: 'leak-severity',
      icon: <Image src={leakSeverity} width={19} />,
    },
  }

  const resourceLevelMap = {
    ResourceLevelJunior: { label: t('初级'), class: 'resource-level-junior' },
    ResourceLevelMiddle: { label: t('中级'), class: 'resource-level-middle' },
    ResourceLevelSenior: { label: t('高级'), class: 'resource-level-senior' },
  }

  useEffect(() => {
    getTemplateList({
      ...searchTemplateParams,
      filterParams: {
        name: String(templateValue.value || ''),
        verificationModeIds: [2], // 验证模式
        sceneIds: [4], // 场景 4 WAF
        ...searchTemplateParams.filterParams,
      },
    })
      .then(res => {
        const newList = res.data.list.map(item => ({
          ...item,
          key: item.templateId,
          executeUpdate: executeType === 3 && executeTemplateLatestCase ? true : false, // 根据执行类型和执行模板最新用例状态设置初始值
        }))
        setTemplateData({
          list: newList,
          total: res.data.total,
        })
      })
      .catch(_ => {})
    const params = { ...searchTemplateParams }
    delete params.page
    delete params.pageSize
    getAllTemplateId({
      filterParams: {
        name: String(templateValue.value || ''),
        verificationModeIds: [2], // 验证模式
        sceneIds: [4], // 场景 4 WAF
        ...params.filterParams,
      },
    })
      .then(res => {
        setTemplateIds(res.data.templateIds)
      })
      .catch(_ => {})
  }, [searchTemplateParams, executeType, executeTemplateLatestCase])

  useEffect(() => {
    setSearchTemplateParams(prev => ({
      ...prev,
      page: 1,
    }))
  }, [templateValue.value])

  // 监听 caseValue 变化并更新 searchCaseParams
  useEffect(() => {
    // 如果正在重置参数，不处理 caseValue 的变化
    if (isResettingRef.current) {
      return
    }
    if (caseValue?.field) {
      const selectedOption = options.find(option => option.value === caseValue.field)
      if (selectedOption) {
        // 对 tagId 类型的值进行特殊处理
        const processedValue =
          caseValue.field === 'tagId'
            ? caseValue?.value
              ? (caseValue.value as string[])?.map(item => item.split('$').slice(1).join('$'))
              : undefined
            : caseValue.value

        setSearchCaseParams(prev => {
          if (prev.filterParams?.[caseValue.field] === processedValue) {
            return prev
          }
          return {
            ...prev,
            page: 1,
            filterParams: {
              [caseValue.field]: processedValue,
            },
          }
        })
      }
    }
  }, [caseValue])

  // 使用 useRef 跟踪上一次的值，避免不必要的重复请求
  const prevSelectedTemplateIdsRef = useRef<string>('')
  const prevSearchCaseParamsRef = useRef<string>('')
  const isResettingRef = useRef<boolean>(false)

  // 当模板ID变化时，重置用例相关的参数为默认值
  useEffect(() => {
    const currentTemplateIdsStr = selectedTemplateIds.join(',')
    const isTemplateIdsChanged = prevSelectedTemplateIdsRef.current !== currentTemplateIdsStr

    if (isTemplateIdsChanged && selectedTemplateIds.length > 0) {
      // 标记正在重置参数
      isResettingRef.current = true
      // 先重置用例搜索值，避免触发caseValue的useEffect设置filterParams
      setCaseValue({
        field: '',
        value: '',
      })
      // 重置用例搜索参数为默认值（包含基础filterParams，明确设置name为undefined）
      setSearchCaseParams({
        page: 1,
        pageSize: 10,
        filterParams: {
          verificationModeId: [2],
          sceneId: [4],
          name: undefined, // 明确设置name为undefined，确保重置时不包含name字段
        },
      })
      // 清空模板已选用例数映射
      setTemplateSelectedCountMap({})
      // 更新模板ID的 ref
      prevSelectedTemplateIdsRef.current = currentTemplateIdsStr
    }
    // 只在模板ID变化时触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateIds.join(',')])

  // 发送用例相关接口请求
  useEffect(() => {
    // 生成当前值的字符串表示用于比较
    const currentTemplateIdsStr = selectedTemplateIds.join(',')
    const currentSearchParamsStr = JSON.stringify({
      page: searchCaseParams.page,
      pageSize: searchCaseParams.pageSize,
      filterParams: searchCaseParams.filterParams,
    })

    // 如果正在重置参数，使用默认参数发送请求
    if (isResettingRef.current) {
      // 清除重置标记
      isResettingRef.current = false
      // 更新 ref 为重置后的值（只包含基础filterParams，name为undefined）
      prevSearchCaseParamsRef.current = JSON.stringify({
        page: 1,
        pageSize: 10,
        filterParams: {
          verificationModeId: [2],
          sceneId: [4],
          name: undefined,
        },
      })
      // 使用重置后的默认参数发送请求（确保filterParams只包含基础字段，name为undefined）
      if (selectedTemplateIds.length > 0) {
        // 构建filterParams，明确设置name为undefined
        const baseFilterParams: Record<string, any> = {
          verificationModeId: [2], // 验证模式
          sceneId: [4], // 场景 4 WAF
          name: undefined, // 明确设置name为undefined
        }
        // 发送请求时，过滤掉undefined值
        const filterParamsForRequest = Object.fromEntries(
          Object.entries(baseFilterParams).filter(([_, value]) => value !== undefined),
        )
        getCaseList({
          page: 1,
          pageSize: 10,
          queryType: 'query_bind',
          templateId: selectedTemplateIds,
          filterParams: filterParamsForRequest,
        })
          .then(res => {
            setCaseData({
              list: res.data.list,
              total: res.data.total,
            })
          })
          .catch(_ => {})
        getResourceIdsByTemplateIds({
          queryType: 'query_bind',
          templateId: selectedTemplateIds,
          filterParams: filterParamsForRequest,
        })
          .then(res => {
            const resourceIds = res.data.resourceIds
            setCaseIds(resourceIds)

            // 检查 resourceIds 是否全部在 selectedCaseIds 中
            const allResourceIdsInSelected = resourceIds.every(id => selectedCaseIds.includes(id))
            if (allResourceIdsInSelected) {
              setSelectedCaseIds(resourceIds)
            }
          })
          .catch(_ => {})
      }
      return
    }

    // 如果值没有变化，不触发请求
    if (
      prevSelectedTemplateIdsRef.current === currentTemplateIdsStr &&
      prevSearchCaseParamsRef.current === currentSearchParamsStr
    ) {
      return
    }

    // 更新 ref
    prevSelectedTemplateIdsRef.current = currentTemplateIdsStr
    prevSearchCaseParamsRef.current = currentSearchParamsStr

    if (selectedTemplateIds.length > 0) {
      getCaseList({
        ...searchCaseParams,
        queryType: 'query_bind',
        templateId: selectedTemplateIds,
        filterParams: {
          verificationModeId: [2], // 验证模式
          sceneId: [4], // 场景 4 WAF
          ...searchCaseParams.filterParams,
        },
      })
        .then(res => {
          setCaseData({
            list: res.data.list,
            total: res.data.total,
          })
        })
        .catch(_ => {})
      const params = { ...searchCaseParams }
      delete params.page
      delete params.pageSize
      getResourceIdsByTemplateIds({
        ...params,
        queryType: 'query_bind',
        templateId: selectedTemplateIds,
        filterParams: {
          verificationModeId: [2], // 验证模式
          sceneId: [4], // 场景 4 WAF
          ...params.filterParams,
        },
      })
        .then(res => {
          const resourceIds = res.data.resourceIds
          setCaseIds(resourceIds)

          // 检查 resourceIds 是否全部在 selectedCaseIds 中
          const allResourceIdsInSelected = resourceIds.every(id => selectedCaseIds.includes(id))
          if (allResourceIdsInSelected) {
            setSelectedCaseIds(resourceIds)
          }
        })
        .catch(_ => {})
    } else {
      setCaseIds([])
      setCaseData({
        list: [],
        total: 0,
      })
      setSelectedCaseIds([])
    }
    console.log('selectedTemplateIds', selectedTemplateIds)
    // 使用更细粒度的依赖项，避免不必要的重复请求
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchCaseParams.page,
    searchCaseParams.pageSize,
    JSON.stringify(searchCaseParams.filterParams),
    selectedTemplateIds.join(','),
  ])

  // 模板更新后更新攻击类型列表
  const prevTemplateIdsForAttackTypeRef = useRef<string>('')
  useEffect(() => {
    if (selectedTemplateIds.length > 0) {
      const currentTemplateIdsStr = selectedTemplateIds.join(',')
      // 如果模板ID没有变化，不触发请求
      if (prevTemplateIdsForAttackTypeRef.current === currentTemplateIdsStr) {
        return
      }
      prevTemplateIdsForAttackTypeRef.current = currentTemplateIdsStr

      const timer = setTimeout(() => {
        // 只在模板ID变化时获取攻击类型列表，避免重复请求
        getAttackTypeList({
          queryType: 'query_type_create_task_resource_list',
          'createTaskResourceListQuery.templateIds': selectedTemplateIds,
        }).catch(_ => {})
        getResourceTagList({
          queryType: 'query_bind',
          templateId: selectedTemplateIds,
          filterParams: {},
        }).catch(_ => {})
      }, 1000)
      return () => clearTimeout(timer)
    }
    return undefined
    // 只在 selectedTemplateIds 变化时触发，不依赖其他状态
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateIds.join(',')])

  // 监听选中的用例变化
  useEffect(() => {
    if (onCaseIdsChange) {
      onCaseIdsChange(selectedCaseIds)
    }
  }, [selectedCaseIds, onCaseIdsChange])

  // 监听选中的模板变化
  useEffect(() => {
    if (onTemplateIdsChange) {
      onTemplateIdsChange(selectedTemplateData)
    }
  }, [selectedTemplateIds, onTemplateIdsChange, selectedTemplateData])

  // 监听执行模板最新用例的变化
  useEffect(() => {
    if (executeType === 3) {
      setTemplateData(prev => ({
        ...prev,
        list: prev.list.map(item => ({
          ...item,
          executeUpdate:
            templateState?.templateSwitchStates?.[item.templateId] ??
            (item.executeUpdate === undefined ? executeTemplateLatestCase : item.executeUpdate),
        })),
      }))
    }
  }, [executeTemplateLatestCase, executeType, templateState?.templateSwitchStates])

  // 监听第一步模板增量更新开关的变化，当关闭时重置第二步的模板开关状态
  useEffect(() => {
    if (executeType === 3 && !executeTemplateLatestCase) {
      // 当第一步的模板增量更新开关关闭时，重置所有模板的开关状态
      const resetTemplateSwitchStates: Record<string, boolean> = {}
      selectedTemplateIds.forEach(templateId => {
        resetTemplateSwitchStates[templateId] = false
      })

      // 更新模板数据中的executeUpdate状态
      setTemplateData(prev => ({
        ...prev,
        list: prev.list.map(item => ({
          ...item,
          executeUpdate: false,
        })),
      }))

      // 更新selectedTemplateData中的executeUpdate状态
      setSelectedTemplateData(prev =>
        prev.map(item => ({
          ...item,
          executeUpdate: false,
        })),
      )

      // 通知父组件重置模板开关状态
      onTemplateStateChange?.({
        templateSwitchStates: resetTemplateSwitchStates,
      })
    }
  }, [executeTemplateLatestCase, executeType, selectedTemplateIds])

  // 监听 caseIds 变化
  useEffect(() => {
    // 如果所有 caseIds 都在 selectedCaseIds 中，则更新全选状态
    if (caseIds?.length > 0 && selectedCaseIds.length > 0) {
      const allSelected = caseIds.every(id => selectedCaseIds.includes(id))
      console.log('All cases selected:', allSelected)
    }
  }, [caseIds, selectedCaseIds])

  // 处理全选所有
  const handleSelectAll = async (checked: boolean) => {
    if (checked) {
      try {
        // 获取所有模板数据
        const response = await getTemplateList({
          ...searchTemplateParams,
          pageSize: templateData.total, // 设置pageSize为总数，一次性获取所有数据
          filterParams: {
            name: String(templateValue.value || ''),
            verificationModeIds: [2], // 验证模式
            sceneIds: [4], // 场景 4 WAF
            ...searchTemplateParams.filterParams,
          },
        })

        const allTemplates = response.data.list.map(item => ({
          ...item,
          key: item.templateId,
          executeUpdate: executeType === 3 && executeTemplateLatestCase ? true : false,
        }))

        setSelectedTemplateIds(templateIds)
        setSelectedTemplateData(allTemplates)
        // 通知父组件更新状态
        onTemplateIdsChange?.(allTemplates)
        onTemplateStateChange?.({
          selectedTemplateIds: templateIds,
          selectedCaseIds: selectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      } catch (error) {
        console.error('Failed to get all templates:', error)
        message.error(t('获取模板列表失败'))
      }
    } else {
      setSelectedTemplateIds([])
      setSelectedTemplateData([])
      // 当取消模板全选时，同时清空用例列表的选中状态
      setSelectedCaseIds([])
      // 通知父组件更新状态
      onTemplateIdsChange?.([])
      onTemplateStateChange?.({
        selectedTemplateIds: [],
        selectedCaseIds: [],
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    }
  }

  // 处理全选当页
  const handleSelectPage = (checked: boolean) => {
    if (checked) {
      const currentPageIds = templateData.list.map(item => item.templateId)
      setSelectedTemplateIds(prev => [...new Set([...prev, ...currentPageIds])])

      // 更新选中的模板数据
      const newSelectedTemplateData = templateData.list.map(item => ({
        ...item,
        executeUpdate: templateState?.templateSwitchStates?.[item.templateId] ?? item.executeUpdate,
      }))

      // 合并之前选中的和当前页选中的模板数据
      const updatedTemplateData = selectedTemplateData
        .filter(item => !templateData.list.some(t => t.templateId === item.templateId))
        .concat(newSelectedTemplateData)
      setSelectedTemplateData(updatedTemplateData)

      // 通知父组件更新状态
      onTemplateIdsChange?.(updatedTemplateData)
      onTemplateStateChange?.({
        selectedTemplateIds: [...new Set([...selectedTemplateIds, ...currentPageIds])],
        selectedCaseIds: selectedCaseIds,
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    } else {
      const currentPageIds = templateData.list.map(item => item.templateId)
      setSelectedTemplateIds(prev => prev.filter(id => !currentPageIds.includes(id)))

      // 更新选中的模板数据，移除当前页的模板
      const updatedTemplateData = selectedTemplateData.filter(
        item => !currentPageIds.includes(item.templateId),
      )
      setSelectedTemplateData(updatedTemplateData)

      // 通知父组件更新状态
      onTemplateIdsChange?.(updatedTemplateData)
      onTemplateStateChange?.({
        selectedTemplateIds: selectedTemplateIds.filter(id => !currentPageIds.includes(id)),
        selectedCaseIds: selectedCaseIds,
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    }
  }

  // 计算是否全选
  const isAllSelected = useMemo(
    () => caseData.total > 0 && selectedCaseIds.length === caseData.total,
    [caseData.total, selectedCaseIds.length],
  )

  // 计算是否部分选中
  const isIndeterminate = useMemo(
    () => selectedCaseIds.length > 0 && selectedCaseIds.length < caseData.total,
    [selectedCaseIds.length, caseData.total],
  )

  // 处理用例全选所有
  const handleCaseSelectAll = async (checked: boolean) => {
    if (checked) {
      // 如果是高频任务且当前选中的用例数加上要新增的用例数超过10个，则不允许全选
      if (executeType === 4 && selectedCaseIds.length + (caseIds?.length || 0) > 10) {
        message.warning(t('高频任务最多只能选择10个用例'))
        return
      }
      const newSelectedCaseIds = [...new Set([...selectedCaseIds, ...(caseIds || [])])]
      setSelectedCaseIds(newSelectedCaseIds)

      // 获取所有用例的完整数据来计算每个模板的已选用例数
      if (caseIds.length > 0) {
        try {
          const params = { ...searchCaseParams }
          delete params.page
          delete params.pageSize
          const res = await getCaseList({
            ...params,
            queryType: 'query_bind',
            templateId: selectedTemplateIds,
            filterParams: {
              verificationModeId: [2],
              sceneId: [4],
              ...params.filterParams,
            },
            page: 1,
            pageSize: caseData.total > 0 ? caseData.total : 1000, // 获取所有用例
          })

          const allCases: CaseItem[] = res?.data?.list || []
          const selectedIdSet = new Set(newSelectedCaseIds)
          const aggregatedSelection = new Map<string, number>()

          // 遍历所有用例，统计每个模板的已选用例数
          allCases.forEach(item => {
            if (!selectedIdSet.has(item.resourceId.toString())) return
            item.templateIds?.forEach(templateId => {
              if (!templateId) return
              const current = aggregatedSelection.get(templateId) || 0
              aggregatedSelection.set(templateId, current + 1)
            })
          })

          // 更新模板已选用例数映射
          const updatedMap: Record<string, number> = {}
          aggregatedSelection.forEach((count, templateId) => {
            updatedMap[templateId] = count
          })
          setTemplateSelectedCountMap(updatedMap)
        } catch (error) {
          console.error('Failed to sync template selection when selecting all use cases:', error)
        }
      }

      onCaseIdsChange?.(newSelectedCaseIds)
      onTemplateStateChange?.({
        selectedTemplateIds: selectedTemplateIds,
        selectedCaseIds: newSelectedCaseIds,
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    } else {
      setSelectedCaseIds([])
      setTemplateSelectedCountMap({})
      onCaseIdsChange?.([])
      onTemplateStateChange?.({
        selectedTemplateIds: selectedTemplateIds,
        selectedCaseIds: [],
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    }
  }

  // 更新模板已选用例数映射
  const updateTemplateSelectedCountMap = useCallback(
    (newSelectedCaseIds: string[]) => {
      // 基于caseData.list计算每个模板的已选用例数
      const updatedMap: Record<string, number> = {}
      const selectedIdSet = new Set(newSelectedCaseIds)

      caseData.list.forEach(item => {
        if (!selectedIdSet.has(item.resourceId.toString())) return
        item.templateIds?.forEach(templateId => {
          if (!templateId) return
          updatedMap[templateId] = (updatedMap[templateId] || 0) + 1
        })
      })

      setTemplateSelectedCountMap(updatedMap)
    },
    [caseData.list],
  )

  // 处理用例全选当页
  const handleCaseSelectPage = (checked: boolean) => {
    if (checked) {
      const currentPageIds = (caseData.list || []).map(item => item.resourceId.toString())
      const newSelectedCaseIds = [...new Set([...selectedCaseIds, ...currentPageIds])]
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
    } else {
      const currentPageIds = (caseData.list || []).map(item => item.resourceId.toString())
      const newSelectedCaseIds = selectedCaseIds.filter(id => !currentPageIds.includes(id))
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
    }
  }

  // 处理模板选择变化
  const handleTemplateSelectionChange = (keys: React.Key[], selectedRows: TemplateItem[]) => {
    const newSelectedTemplateIds = keys.map(key => key.toString())
    setSelectedTemplateIds(newSelectedTemplateIds)
    onTemplateIdsChange?.(selectedRows)
    onTemplateStateChange?.({
      selectedTemplateIds: newSelectedTemplateIds,
      templateSwitchStates: templateState?.templateSwitchStates || {},
    })
  }

  // 处理单个模板的开关状态变化
  const handleTemplateSwitchChange = (templateId: string, checked: boolean) => {
    // 只有当第一步的模板增量更新开关打开，且是周期任务时，才能操作
    if (executeType !== 3 || !executeTemplateLatestCase) return
    setTemplateData(prev => ({
      ...prev,
      list: prev.list.map(item =>
        item.templateId === templateId ? { ...item, executeUpdate: checked } : item,
      ),
    }))
    // 更新 selectedTemplateData 中的 executeUpdate 值
    setSelectedTemplateData(prev =>
      prev.map(item =>
        item.templateId === templateId ? { ...item, executeUpdate: checked } : item,
      ),
    )
    // 通知父组件更新状态
    onTemplateStateChange?.({
      templateSwitchStates: {
        ...templateState?.templateSwitchStates,
        [templateId]: checked,
      },
    })
  }

  // 模板列表列定义
  const templateColumns: ColumnType<TemplateItem>[] = [
    {
      title: t('模板名称'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Tooltip title={text}>
            <span
              style={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {text}
            </span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('模板类型'),
      dataIndex: 'originType',
      key: 'originType',
      filteredValue: searchTemplateParams?.filterParams?.originType
        ? searchTemplateParams?.filterParams?.originType
        : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择模板类型')}
            value={props.selectedKeys as string[]}
            open={templateTypeSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setTemplateTypeSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setTemplateTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('系统内置'), value: 'OriginTypeSystem' },
              { label: t('手动添加'), value: 'OriginTypeCustom' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (originType: string) => (
        <Tag color={originType === 'OriginTypeSystem' ? '#1f8fa7' : '#f77b83'}>
          {originType === 'OriginTypeSystem' ? t('系统内置') : t('手动添加')}
        </Tag>
      ),
    },
    {
      title: t('执行更新'),
      dataIndex: 'executeUpdate',
      key: 'executeUpdate',
      width: 80,
      render: (_, record) => {
        // 获取该模板的已选用例数
        // 如果已全选所有用例（selectedCaseIds.length === caseData.total），优先使用维护的映射数据
        // 否则基于当前页数据计算
        let templateSelectedCount = 0
        const isAllCasesSelectedForAll =
          selectedCaseIds.length === caseData.total && caseData.total > 0

        if (isAllCasesSelectedForAll && templateSelectedCountMap[record.templateId] !== undefined) {
          // 全选所有时，使用维护的映射数据（基于所有用例数据计算）
          templateSelectedCount = templateSelectedCountMap[record.templateId]
        } else {
          // 从caseData中找出属于该模板的所有用例（当前页）
          const templateCases = caseData.list.filter(caseItem =>
            caseItem.templateIds?.includes(record.templateId),
          )
          // 计算该模板下已选中的用例数（当前页）
          const currentPageSelectedCount = templateCases.filter(caseItem =>
            selectedCaseIds.includes(caseItem.resourceId.toString()),
          ).length

          // 如果当前页属于该模板的所有用例都被选中，且已选中的用例数等于模板总用例数，则认为全部选中
          if (
            templateCases.length > 0 &&
            currentPageSelectedCount === templateCases.length &&
            currentPageSelectedCount === record.resourceAmount
          ) {
            templateSelectedCount = record.resourceAmount
          } else {
            // 否则，基于当前页数据计算（可能不准确，但这是最佳近似）
            templateSelectedCount = currentPageSelectedCount
          }
        }

        // 检查该模板下的所有用例是否都被选中
        // 参考index.tsx的逻辑：record.resourceSelectedAmount === record.resourceAmount
        const isAllCasesSelected =
          record.resourceAmount > 0 && templateSelectedCount === record.resourceAmount

        // 优先使用保存的状态，如果没有则使用当前状态
        const isChecked =
          templateState?.templateSwitchStates?.[record.templateId] ?? record.executeUpdate
        // 只有当第一步的模板增量更新开关打开，且是周期任务，且该模板被选中，且该模板下的所有用例都被选中时，开关才能操作
        // 参考index.tsx的逻辑：!(form.getFieldValue('isLatest') && selectedRowKeys.includes(record.templateId)) || record.resourceAmount === 0 || record.resourceSelectedAmount !== record.resourceAmount
        const isDisabled =
          executeType !== 3 ||
          !executeTemplateLatestCase ||
          !selectedTemplateIds.includes(record.templateId) ||
          record.resourceAmount === 0 ||
          !isAllCasesSelected

        return (
          <div onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
            <BasSwitch
              checked={isChecked}
              disabled={isDisabled}
              onChange={(checked, e) => {
                e.stopPropagation()
                if (isDisabled) return
                handleTemplateSwitchChange(record.templateId, checked)
              }}
            />
          </div>
        )
      },
    },
    {
      title: t('全部|已选用例数'),
      dataIndex: 'TemplateSortFieldResourceAmount',
      key: 'TemplateSortFieldResourceAmount',
      sorter: (a: TemplateItem, b: TemplateItem) => a.resourceAmount - b.resourceAmount,
      render: (_, record: TemplateItem) => {
        // 优先使用维护的映射数据（全选所有时使用），否则从caseData中计算
        let selectedCount = 0
        if (templateSelectedCountMap[record.templateId] !== undefined) {
          selectedCount = templateSelectedCountMap[record.templateId]
        } else {
          // 从caseData中找出属于该模板的所有用例
          const templateCases = caseData.list.filter(caseItem =>
            caseItem.templateIds?.includes(record.templateId),
          )
          // 计算该模板下已选中的用例数
          selectedCount = templateCases.filter(caseItem =>
            selectedCaseIds.includes(caseItem.resourceId.toString()),
          ).length
        }
        return `${record.resourceAmount} | ${selectedCount}`
      },
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      sorter: (a: TemplateItem, b: TemplateItem) =>
        new Date(a.updateTime).getTime() - new Date(b.updateTime).getTime(),
      render: (updateTime: string) => (
        <span>{updateTime?.split('T')[0] ? updateTime.split('T')[0] : '-'}</span>
      ),
    },
  ]

  // 已选用例列表列定义
  const selectedCaseColumns = [
    {
      title: t('用例名称'),
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      render: (text: string, record: any) => (
        <Space>
          <Tooltip title={text}>
            <span
              style={{
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
              }}
            >
              {text}
            </span>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('风险等级'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: '15%',
      filteredValue: searchCaseParams?.filterParams?.riskLevel
        ? searchCaseParams?.filterParams?.riskLevel
        : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择风险等级')}
            value={props.selectedKeys as string[]}
            open={riskLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setRiskLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setRiskLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('低危'), value: 'RiskLevelLow' },
              { label: t('中危'), value: 'RiskLevelMiddle' },
              { label: t('高危'), value: 'RiskLevelHigh' },
              { label: t('严重'), value: 'RiskLevelGross' },
            ]}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (text: string) => (
        <div>
          {riskLevelMap[text].icon}
          <span className={riskLevelMap[text].class}>{riskLevelMap[text].label}</span>
        </div>
      ),
    },
    {
      title: t('用例分级'),
      dataIndex: 'resourceLevel',
      key: 'resourceLevel',
      width: '15%',
      filteredValue: searchCaseParams?.filterParams?.resourceLevel
        ? searchCaseParams?.filterParams?.resourceLevel
        : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            mode="multiple"
            allowClear
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            placeholder={t('请选择用例分级')}
            value={props.selectedKeys as string[]}
            open={resourceLevelSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setResourceLevelSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setResourceLevelSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={[
              { label: t('初级'), value: 'ResourceLevelJunior' },
              { label: t('中级'), value: 'ResourceLevelMiddle' },
              { label: t('高级'), value: 'ResourceLevelSenior' },
            ]}
          />
        </div>
      ),

      render: (level: string) => (
        <span className={resourceLevelMap[level].class}>{resourceLevelMap[level].label}</span>
      ),
    },
    {
      title: t('攻击类型'),
      dataIndex: 'attackTypeId',
      key: 'attackTypeId',
      width: '15%',
      filteredValue: searchCaseParams?.filterParams?.attackTypeId
        ? searchCaseParams?.filterParams?.attackTypeId
        : '',
      filterDropdown: (props: FilterDropdownProps) => (
        <div style={{ padding: 8, width: 300 }}>
          <Select
            allowClear
            mode="multiple"
            maxTagCount={'responsive'}
            style={{ width: '100%' }}
            filterOption={(input, option) => {
              const attackTypeName =
                attackTypeList?.find(item => item.id === option?.value)?.name || ''
              return attackTypeName.toLowerCase().includes(input.toLowerCase())
            }}
            placeholder={t('请选择攻击类型')}
            value={props.selectedKeys as string[]}
            open={attackTypeSelectOpen}
            getPopupContainer={() => document.body as HTMLElement}
            dropdownClassName="table-filter-dropdown"
            onDropdownVisibleChange={setAttackTypeSelectOpen}
            onChange={value => {
              props.setSelectedKeys(value)
            }}
            dropdownRender={menu => (
              <div style={{ marginBottom: '6px' }}>
                {menu}
                <Divider style={{ margin: '3px 0 6px 0' }} />
                <Space
                  className="space-btn"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  <Button
                    type="default"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.clearFilters?.()
                    }}
                  >
                    {t('重置')}
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    style={{ letterSpacing: '-2px', padding: '0 12px' }}
                    onClick={() => {
                      props.confirm()
                      setAttackTypeSelectOpen(false)
                    }}
                  >
                    {t('确定')}
                  </Button>
                </Space>
              </div>
            )}
            options={attackTypeList?.map(item => ({
              label: item.name,
              value: item.id,
            }))}
          />
        </div>
      ),

      filterIcon: (filtered: boolean) => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <FilterIcon type="icon-a-shaixuan1" $filtered={filtered} />
        </Tooltip>
      ),

      render: (attackTypeId: string) => {
        const attackType = attackTypeList.find(item => item.id === attackTypeId)
        return (
          <Text ellipsis={{ tooltip: true }} style={{ maxWidth: 100 }}>
            {attackType?.name || '-'}
          </Text>
        )
      },
    },
    {
      title: t('更新时间'),
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '15%',
      sorter: (a, b) => new Date(a.updateTime).getTime() - new Date(b.updateTime).getTime(),
      render: (updateTime: string) => (
        <span>{updateTime?.split('T')[0] ? updateTime.split('T')[0] : '-'}</span>
      ),
    },
    {
      title: t('操作'),
      key: 'action',
      width: '10%',
      render: (_, record) => (
        <span
          onClick={() => {
            setDetailVisible({ visible: true, resourceId: record.resourceId })
          }}
          className="detail-btn"
        >
          {t('详情')}
        </span>
      ),
    },
  ]

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      // 获取表单数据
      const values = await form.validateFields()
      console.log('模板表单数据:', values)
      // 这里可以添加额外的处理逻辑
    } catch (error) {
      console.error('表单验证失败:', error)
      // 这里可以添加错误提示
    }
  }

  const handleTemplateTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log('filters', filters)
    setSearchTemplateParams({
      ...searchTemplateParams,
      page: pagination.current,
      pageSize: pagination.pageSize,
      filterParams: {
        sortField: sorter.field,
        sortOrder:
          sorter.order === 'descend'
            ? FilterParamsSortOrder.SortOrderDescending
            : sorter.order === 'ascend'
            ? FilterParamsSortOrder.SortOrderAscending
            : FilterParamsSortOrder.SortOrderUnspecified,
        originType: filters.originType,
      },
    })
    // 保持选中状态
    setSelectedTemplateIds(prev => [...prev])
    // 保持模板选中状态
    onTemplateStateChange?.({
      selectedTemplateIds: selectedTemplateIds,
      selectedCaseIds: selectedCaseIds,
      templateSwitchStates: templateState?.templateSwitchStates || {},
    })
  }

  const handleCaseTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log('filters', filters)
    const processedValue =
      caseValue.field === 'tagId'
        ? caseValue?.value
          ? (caseValue.value as string[])?.map(item => item.split('$').slice(1).join('$'))
          : undefined
        : caseValue.value
    setSearchCaseParams({
      ...searchCaseParams,
      page: pagination.current,
      pageSize: pagination.pageSize,
      filterParams: {
        sortField: sorter.field,
        sortOrder:
          sorter.order === 'descend'
            ? FilterParamsSortOrder.SortOrderDescending
            : sorter.order === 'ascend'
            ? FilterParamsSortOrder.SortOrderAscending
            : FilterParamsSortOrder.SortOrderUnspecified,
        ...filters,
        [caseValue.field]: processedValue,
      },
    })
    // 保持选中状态
    setSelectedCaseIds(prev => [...prev])
    // 保持模板选中状态
    onTemplateStateChange?.({
      selectedTemplateIds: selectedTemplateIds,
      selectedCaseIds: selectedCaseIds,
      templateSwitchStates: templateState?.templateSwitchStates || {},
    })
  }

  const saveTemplateSubmit = values => {
    saveCaseToTemplate({
      name: values.name,
      resourceIds: selectedCaseIds,
      verificationModeIds: ['2'],
      sceneIds: ['4'],
    })
      .then(() => {
        setSaveTemplateModal(false)
      })
      .catch(_ => {})
  }

  const templateRowSelection = {
    selectedRowKeys: selectedTemplateIds,
    onChange: (keys: React.Key[], selectedRows: TemplateItem[]) => {
      const newSelectedTemplateIds = keys.map(key => key.toString())

      // 检查是否是"全选所有"的情况
      const isSelectAllAllMode =
        newSelectedTemplateIds.length === templateIds.length && templateIds.length > 0

      // 如果是"全选所有"的情况，直接使用所有模板数据
      if (isSelectAllAllMode) {
        setSelectedTemplateIds(newSelectedTemplateIds)
        // 通知父组件更新状态
        onTemplateStateChange?.({
          selectedTemplateIds: newSelectedTemplateIds,
          selectedCaseIds: selectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
        return
      }

      setSelectedTemplateIds(newSelectedTemplateIds)

      // 更新选中的模板数据
      const newSelectedTemplateData = templateData.list
        .filter(item => newSelectedTemplateIds.includes(item.templateId))
        .map(item => ({
          ...item,
          executeUpdate:
            templateState?.templateSwitchStates?.[item.templateId] ?? item.executeUpdate,
        }))

      // 合并之前选中的和当前页选中的模板数据
      const updatedTemplateData = selectedTemplateData
        .filter(item => !templateData.list.some(t => t.templateId === item.templateId))
        .concat(newSelectedTemplateData)
      setSelectedTemplateData(updatedTemplateData)

      // 通知父组件更新状态
      onTemplateIdsChange?.(updatedTemplateData)
      onTemplateStateChange?.({
        selectedTemplateIds: newSelectedTemplateIds,
        selectedCaseIds: selectedCaseIds,
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    },
    onSelectAll: (selected: boolean) => {
      // 检查是否是"全选所有"的情况
      const isSelectAllAllMode =
        selectedTemplateIds.length === templateIds.length && templateIds.length > 0

      if (selected) {
        // 获取当前页的所有模板ID
        const currentPageIds = templateData.list.map(item => item.templateId)

        // 如果是"全选所有"的情况，直接使用所有模板ID，不进行合并操作
        if (isSelectAllAllMode) {
          // 不执行任何操作，因为handleSelectAll已经处理了
          return
        }

        // 合并当前页选中的ID和之前已选中的ID
        const newSelectedIds = [...new Set([...selectedTemplateIds, ...currentPageIds])]
        setSelectedTemplateIds(newSelectedIds)

        // 更新选中的模板数据
        const newSelectedTemplateData = templateData.list.map(item => ({
          ...item,
          executeUpdate:
            templateState?.templateSwitchStates?.[item.templateId] ?? item.executeUpdate,
        }))

        // 合并之前选中的和当前页选中的模板数据
        const updatedTemplateData = selectedTemplateData
          .filter(item => !templateData.list.some(t => t.templateId === item.templateId))
          .concat(newSelectedTemplateData)
        setSelectedTemplateData(updatedTemplateData)

        // 通知父组件更新状态
        onTemplateIdsChange?.(updatedTemplateData)
        onTemplateStateChange?.({
          selectedTemplateIds: newSelectedIds,
          selectedCaseIds: selectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      } else {
        // 获取当前页的所有模板ID
        const currentPageIds = templateData.list.map(item => item.templateId)
        // 从已选中的ID中移除当前页的ID
        const newSelectedIds = selectedTemplateIds.filter(id => !currentPageIds.includes(id))
        setSelectedTemplateIds(newSelectedIds)

        // 更新选中的模板数据，移除当前页的模板
        const updatedTemplateData = selectedTemplateData.filter(
          item => !currentPageIds.includes(item.templateId),
        )
        setSelectedTemplateData(updatedTemplateData)

        // 通知父组件更新状态
        onTemplateIdsChange?.(updatedTemplateData)
        onTemplateStateChange?.({
          selectedTemplateIds: newSelectedIds,
          selectedCaseIds: selectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      }
    },
    onSelect: (record: TemplateItem, selected: boolean) => {
      if (selected) {
        // 添加选中的模板ID到已选中列表
        const newSelectedIds = [...new Set([...selectedTemplateIds, record.templateId])]
        setSelectedTemplateIds(newSelectedIds)

        // 更新选中的模板数据
        const newTemplate = {
          ...record,
          executeUpdate:
            templateState?.templateSwitchStates?.[record.templateId] ?? record.executeUpdate,
        }
        const updatedTemplateData = selectedTemplateData
          .filter(item => item.templateId !== record.templateId)
          .concat(newTemplate)
        setSelectedTemplateData(updatedTemplateData)

        // 通知父组件更新状态
        onTemplateIdsChange?.(updatedTemplateData)
        onTemplateStateChange?.({
          selectedTemplateIds: newSelectedIds,
          selectedCaseIds: selectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      } else {
        // 从已选中列表中移除取消选中的模板ID
        const newSelectedIds = selectedTemplateIds.filter(id => id !== record.templateId)
        setSelectedTemplateIds(newSelectedIds)

        // 更新选中的模板数据
        const updatedTemplateData = selectedTemplateData.filter(
          item => item.templateId !== record.templateId,
        )
        setSelectedTemplateData(updatedTemplateData)

        // 通知父组件更新状态
        onTemplateIdsChange?.(updatedTemplateData)
        onTemplateStateChange?.({
          selectedTemplateIds: newSelectedIds,
          selectedCaseIds: selectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      }
    },
    getCheckboxProps: (record: TemplateItem) => ({
      disabled: record.type === 'system',
    }),
  }

  const caseRowSelection = {
    selectedRowKeys: selectedCaseIds,
    onChange: (keys: React.Key[], selectedRows: CaseItem[]) => {
      // 如果是高频任务且选中的用例数超过10个，则不允许选择
      if (executeType === 4 && keys.length > 10) {
        message.warning(t('高频任务最多只能选择10个用例'))
        return
      }
      const newSelectedCaseIds = keys.map(key => key.toString())
      setSelectedCaseIds(newSelectedCaseIds)
      updateTemplateSelectedCountMap(newSelectedCaseIds)
      onCaseIdsChange?.(newSelectedCaseIds)
      onTemplateStateChange?.({
        selectedTemplateIds: selectedTemplateIds,
        selectedCaseIds: newSelectedCaseIds,
        templateSwitchStates: templateState?.templateSwitchStates || {},
      })
    },
    onSelectAll: (selected: boolean) => {
      if (selected) {
        // 如果是高频任务且当前选中的用例数加上当前页的用例数超过10个，则不允许全选
        if (executeType === 4 && selectedCaseIds.length + (caseData.list?.length || 0) > 10) {
          // message.warning('高频任务最多只能选择10个用例')
          return
        }
        const currentPageIds = (caseData.list || []).map(item => item.resourceId.toString())
        const newSelectedCaseIds = [...new Set([...selectedCaseIds, ...currentPageIds])]
        setSelectedCaseIds(newSelectedCaseIds)
        updateTemplateSelectedCountMap(newSelectedCaseIds)
        onCaseIdsChange?.(newSelectedCaseIds)
        onTemplateStateChange?.({
          selectedTemplateIds: selectedTemplateIds,
          selectedCaseIds: newSelectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      } else {
        const currentPageIds = (caseData.list || []).map(item => item.resourceId.toString())
        const newSelectedCaseIds = selectedCaseIds.filter(id => !currentPageIds.includes(id))
        setSelectedCaseIds(newSelectedCaseIds)
        updateTemplateSelectedCountMap(newSelectedCaseIds)
        onCaseIdsChange?.(newSelectedCaseIds)
        onTemplateStateChange?.({
          selectedTemplateIds: selectedTemplateIds,
          selectedCaseIds: newSelectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      }
    },
    onSelect: (record: CaseItem, selected: boolean) => {
      // 如果是高频任务且当前选中的用例数已经达到10个，则不允许继续选择
      if (executeType === 4 && selected && selectedCaseIds.length >= 10) {
        message.warning(t('高频任务最多只能选择10个用例'))
        return
      }
      const recordId = record.resourceId.toString()
      if (selected) {
        const newSelectedCaseIds = [...selectedCaseIds, recordId]
        setSelectedCaseIds(newSelectedCaseIds)
        updateTemplateSelectedCountMap(newSelectedCaseIds)
        onCaseIdsChange?.(newSelectedCaseIds)
        onTemplateStateChange?.({
          selectedTemplateIds: selectedTemplateIds,
          selectedCaseIds: newSelectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      } else {
        const newSelectedCaseIds = selectedCaseIds.filter(id => id !== recordId)
        setSelectedCaseIds(newSelectedCaseIds)
        updateTemplateSelectedCountMap(newSelectedCaseIds)
        onCaseIdsChange?.(newSelectedCaseIds)
        onTemplateStateChange?.({
          selectedTemplateIds: selectedTemplateIds,
          selectedCaseIds: newSelectedCaseIds,
          templateSwitchStates: templateState?.templateSwitchStates || {},
        })
      }
    },
    getCheckboxProps: (record: CaseItem) => ({
      disabled:
        record.type === 'system' ||
        (executeType === 4 &&
          selectedCaseIds.length >= 10 &&
          !selectedCaseIds.includes(record.resourceId.toString())),
    }),
  }

  const options = [
    {
      label: t('用例名称'),
      value: 'name',
      type: 'input' as const,
    },
    {
      label: t('用例标签'),
      value: 'tagId',
      type: 'treeSelect' as const,
      options: TagSearchOption(
        resourceTagList.map(item => ({
          ...item,
          bindTotal: item?.tagCategoryNum,
          tagList: item?.tagList?.map(child => ({
            ...child,
            bindTotal: child?.tagNum,
          })),
        })),
      ),
    },
  ]

  return (
    <Container>
      <Form form={form} onFinish={handleSubmit}>
        <div className="template-section">
          <div style={{ margin: '10px 0' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px',
              }}
            >
              <div className="section-title">{t('模板选择')}</div>
              <div style={{ marginRight: 16 }}>
                <FilterFormSelect
                  filterOptions={[
                    {
                      label: t('模板名称'),
                      value: 'name',
                      type: 'input' as const,
                    },
                  ]}
                  value={templateValue}
                  onChange={newValue => {
                    setTemplateValue(newValue)
                  }}
                />
              </div>
            </div>
          </div>
          <div className="search-area">
            <Checkbox
              checked={selectedTemplateIds.length === templateIds.length && templateIds.length > 0}
              indeterminate={
                selectedTemplateIds.length > 0 && selectedTemplateIds.length < templateIds.length
              }
              onChange={() => handleSelectAll(selectedTemplateIds.length !== templateIds.length)}
            >
              {t('全选所有')}
            </Checkbox>
          </div>
          <Table
            columns={templateColumns}
            dataSource={templateData.list}
            loading={templateLoading}
            rowSelection={{
              ...templateRowSelection,
              preserveSelectedRowKeys: true, // 添加这个属性来保持选中状态
            }}
            rowKey="templateId"
            pagination={{
              total: templateData.total,
              current: searchTemplateParams.page,
              pageSize: searchTemplateParams.pageSize,
              showSizeChanger: true,
              showTotal: (total, range) => (
                <span>
                  {t('共')}
                  {total}
                  {t('条')}
                  <span style={{ marginLeft: 10 }} />
                </span>
              ),
            }}
            onChange={handleTemplateTableChange}
          />
        </div>

        <div className="case-section">
          <div className="header-row">
            <div className="section-title">
              {t('已选用例 (')}
              {selectedCaseIds.length})
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <FilterFormSelect
                filterOptions={options}
                value={caseValue}
                onChange={newValue => {
                  console.log('newValue', newValue)
                  setCaseValue(newValue)
                }}
              />

              <Button
                type="primary"
                size="small"
                className="save-template-btn"
                onClick={() => setSaveTemplateModal(true)}
                disabled={selectedCaseIds.length === 0}
              >
                {t('保存为模板')}
              </Button>
            </div>
          </div>
          <div className="search-area">
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              onChange={() => handleCaseSelectAll(!isAllSelected)}
            >
              {t('全选所有')}
            </Checkbox>
          </div>
          <Table
            className="case-table"
            columns={selectedCaseColumns}
            rowSelection={{
              ...caseRowSelection,
              preserveSelectedRowKeys: true, // 添加这个属性来保持选中状态
            }}
            dataSource={caseData.list || []}
            loading={caseLoading}
            rowKey="resourceId"
            pagination={{
              total: caseData.total,
              current: searchCaseParams.page,
              pageSize: searchCaseParams.limit,
              showSizeChanger: true,
              showTotal: (total, range) => (
                <span>
                  {t('共')}
                  {total}
                  {t('条')}
                  <span style={{ marginLeft: 10 }} />
                </span>
              ),
            }}
            onChange={handleCaseTableChange}
          />
        </div>
      </Form>
      <Modal
        title={<SignTitle show>{t('保存为模板')}</SignTitle>}
        visible={saveTemplateModal}
        onOk={() => {
          form.submit()
        }}
        onCancel={() => {
          form.resetFields()
          setSaveTemplateModal(false)
        }}
        confirmLoading={saveCaseToTemplateLoading}
        centered
      >
        <Form form={form} layout={'vertical'} onFinish={saveTemplateSubmit}>
          <Form.Item
            name="name"
            rules={[
              { required: true, message: t('请输入') },
              {
                max: 99,
                message: t('模板名称最多99字'),
              },
              {
                pattern: /^(?=.*\S).+$/,
                message: t('模板名称名称不能全部为空格!'),
              },
            ]}
            label={t('模板名称')}
          >
            <Input placeholder={t('请输入')} maxLength={99} />
          </Form.Item>
        </Form>
      </Modal>
      <CaseDetail
        visible={detailVisible.visible}
        onClose={() => setDetailVisible({ visible: false, resourceId: '' })}
        resourceId={detailVisible.resourceId}
        attackTypeList={attackTypeList}
      />
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  min-width: 1180px;
  padding: 0 16px;
  .case-table {
    .detail-btn {
      color: #4238c9;
      cursor: pointer;
      &:hover {
        color: #6a5ed6;
      }
    }
  }
  .ant-table-measure-row {
    display: none;
  }
  .ant-table-column-sorters {
    justify-content: flex-start;
    .ant-table-column-sorter {
      padding-top: 4px;
      margin-left: 5px;
    }
    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;
    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-form {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .template-section {
    width: 50%;
  }
  .case-section {
    width: 50%;
  }
  .template-section,
  .case-section {
    height: 100%;
    background: #ffffff;
    border-radius: 8px;
    border: 1px solid #e5e6eb;
    padding: 0;
    min-width: 650px; // 防止flex子项溢出
    .ant-pagination {
      margin-bottom: 20px;
      margin-right: 16px;
    }
    .ant-table {
      padding: 0 16px;
    }
  }
  .filter-form-container {
    margin-bottom: 16px;
    .filter-search {
      width: 100%;
      padding: 0 16px;
      .filter-form {
        width: 100%;
      }
    }
  }

  .section-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    padding: 0 16px;
  }
  .template-title {
    margin: 10px 0 18px;
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 10px;
    .save-template-btn {
      height: 30px;
      line-height: 25px;
      margin-right: 16px;
      margin-left: 8px;
    }
  }

  .search-area {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    padding: 0 16px;
    .ant-checkbox-wrapper {
      margin-right: 16px;
    }
  }

  .ant-table-wrapper {
    .ant-table-thead {
      > tr > th {
        background: #f7f8fa;
        height: 48px;
        padding: 12px 8px;
        &::before {
          display: none;
        }
      }
    }
    .ant-table-tbody {
      > tr > td {
        height: 48px;
        padding: 12px 8px;
      }
    }
  }

  .ant-tag {
    border-radius: 4px;
    padding: 0 8px;
    margin-right: 0;
  }

  // 表格内容样式优化
  .ant-table {
    font-size: 14px;
    line-height: 22px;

    .ant-table-cell {
      padding: 12px 8px;
    }
  }

  // 分页样式优化
  .ant-pagination {
    margin: 16px 0 0;
  }

  // 标签样式
  .system-tag {
    background: #f2f3f5;
    border-radius: 2px;
    padding: 2px 8px;
    font-size: 12px;
    color: #86909c;
  }

  // 开关样式
  .leak-low {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #e1ebfe;
    color: #6f9fff;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-center {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffedce;
    color: #ffa709;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-high {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffe1e1;
    color: #ff4141;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-severity {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #f5d9d9;
    color: #b70000;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-junior {
    display: inline-block;
    background: #6f9fff;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-middle {
    display: inline-block;
    background: #ffa709;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-senior {
    display: inline-block;
    background: #f44f4f;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
`

const FilterIcon = styled(IconFont)<{ $filtered?: boolean }>`
  font-size: 14px;
  color: ${props => (props.$filtered ? '#4238C9' : '#999')};
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #4238c9;
  }
`
